import axios from "axios";
import moment from "moment";
import { AuditTrailActions } from "./components/shared/constants/AuditTrailActions";
import {
  getPassData,
  isUserDetailsLoaded,
  markUserDetailsLoaded,
} from "./components/shared/services/AuthSessionService";

/**
 * Strips CR and LF characters from header values to prevent HTTP response
 * splitting / header injection attacks (see CVE-2026-40175).
 * @param {*} value - The header value to sanitize.
 * @returns {string} The sanitized string, or the original value if non-string.
 */
const sanitizeHeaderValue = (value) =>
  typeof value === "string" ? value.replace(/[\r\n]/g, "") : value;

export default {
  setupInterceptors: (props) => {
    global.REACT_APP_API_ENDPOINT = "/cgapi";
    const APIsTrackingForAuditLog = AuditTrailActions;
    markUserDetailsLoaded(false);

    const hostname = window.location.hostname;

    switch (hostname) {
      case "localhost":
      case "webapp-essui-eb.eitcg.devl-ess.aws.fanniemae.com":
      case "cgo-ui-dev-01-eb.eitcg.devl-sfbu.aws.fanniemae.com":
        global.REACT_APP_API_ENDPOINT = "http://localhost:5000/cgapi";
        break;
      case "ess-ui-devl-eb.eitcg.devl-ess.aws.fanniemae.com":
        global.REACT_APP_API_ENDPOINT = "https://ess-ven-devl-eb.eitcg.devl-ess.aws.fanniemae.com/cgapi";
        break;
    }

    //Set headers for all request
    axios.interceptors.request.use(
      (request) => {
        // console.log("In request interceptor....");
        const role = sessionStorage.getItem("role");
        const requestUrl = request.url || "";
        const normalizedPath = requestUrl.startsWith("http")
          ? new URL(requestUrl).pathname
          : requestUrl.startsWith("/")
            ? requestUrl
            : `/${requestUrl}`;
        const isUserDetailsRequest = normalizedPath === "/userdetails";
        const isBackendRequest = normalizedPath.startsWith("/cgapi");
        const authReady = isUserDetailsLoaded();
        const passData = getPassData();
        const hasPassData = !!passData;

        // Block only during initial auth bootstrap.
        // If passData already exists, allow backend calls while /userdetails re-validates.
        if (isBackendRequest && !isUserDetailsRequest && !authReady && !hasPassData) {
          return Promise.reject(new Error("Backend request blocked until /userdetails succeeds"));
        }

        let url = "";
        if (hostname == "localhost" || hostname == "webapp-essui-eb.eitcg.devl-ess.aws.fanniemae.com") {
          url = request.url;
        } else {
          url = "https://" + hostname + request.url;
        }

        if (url) {
          const method = new URL(url).pathname;
          if (APIsTrackingForAuditLog.findIndex(element => method.includes(element)) >= 0) {
            if (request?.method == "delete") {
              let data = method.split('/').filter(Boolean).pop() ? method.split('/').filter(Boolean).pop() : "No Data";
              sessionStorage.setItem("requestdata", data);
            } else {
              if (request?.data) {
                sessionStorage.setItem("requestdata", JSON.stringify(request.data));
              } else {
                sessionStorage.setItem("requestdata", "No Data");
              }
            }
            request.headers['request-startTime'] = new Date().getTime();
          }
        }
        const sanitizedRole = sanitizeHeaderValue(role);
        const hasValidRole =
          typeof sanitizedRole === "string" &&
          sanitizedRole.trim() !== "" &&
          sanitizedRole !== "undefined" &&
          sanitizedRole !== "null";

        if (hasValidRole) {
          request.headers["x-cg-assumed-role"] = sanitizedRole;
          axios.defaults.headers.common["x-cg-assumed-role"] = sanitizedRole;
        } else {
          delete request.headers["x-cg-assumed-role"];
          delete axios.defaults.headers.common["x-cg-assumed-role"];
        }
        request.hostname = "";
        return request;
      },
      (error) => {
        console.error(error);
        return Promise.reject(error);
      }
    );

    axios.interceptors.response.use(
      (response) => {
        const reponseTime = new Date();
        sessionStorage.setItem("lastInteractionTime", moment());
        if (response.status === 200) {
        /* Audit trails workflo for All the POST APIS and other important APIs Admin user wants to track */
          let url = "";
          if (hostname === "localhost" || hostname === "webapp-essui-eb.eitcg.devl-ess.aws.fanniemae.com") {
            url = response.request?.responseURL;
          } else {
            url = "https://" + hostname + response.request?.responseURL;
          }
          if (url) {
            const method = new URL(url).pathname;
            if (APIsTrackingForAuditLog.findIndex(element => method.includes(element)) >= 0) {
              const api = APIsTrackingForAuditLog.find(element => method.includes(element));
              if (api !== 'postFileuploadContext') {
                const passData = getPassData();
                let requestData = 'No Data';
                // console.log(sessionStorage.getItem("requestdata"))
                if (sessionStorage.getItem("requestdata")) {
                  if (sessionStorage.getItem("requestdata") == 'No Data'){
                    requestData = sessionStorage.getItem("requestdata");
                  } else {
                    try {
                     if (JSON.parse(sessionStorage.getItem("requestdata"))) {
                        requestData = JSON.parse(sessionStorage.getItem("requestdata"));
                      } else{
                        requestData = sessionStorage.getItem("requestdata");
                     }
                    }
                    catch(e) {
                      requestData = sessionStorage.getItem("requestdata");
                    }
                  }
                } 
                const startTime = response.config.headers['request-startTime'];
                const duration = new Date().getTime() - startTime;
                const requestTime = new Date().setMilliseconds(new Date().getMilliseconds() - duration);
                const request = {
                  'id': null,
                  'action': response?.config?.method.toUpperCase(),
                  'function': api,
                  'requestUser': passData?.user,
                  'payload': JSON.stringify(requestData),
                  'requestTime': new Date(requestTime),
                  'responseTime': reponseTime,
                  'auditTimestamp': new Date()
                }
                axios
                .post(global.REACT_APP_API_ENDPOINT + `/auditdata/saveAuditTrail`, request)
                .then((response) => {
                  sessionStorage.removeItem("requestdata");
                })
                .catch((error) => {
                  throw Error(error);
                });
              } else {
                sessionStorage.removeItem("requestdata");
              }
            }
          }
        }
        return response;
      },
      function (error) {
        if ( window.location.href.indexOf("condogo/cgapi/?cdxRedirectPath=/") > -1) {
          window.location.replace(window.location.protocol +
            "//" + window.location.host + "/cgapi/?cdxRedirectPath=/");
        }
        if (error && error.response) {
          const responseUrl = error.request?.responseURL || "";
          if (!responseUrl.includes("cgapi") && error.response.status == 302) {
            window.location.replace(window.location.protocol +
              "//" + window.location.host + "/cgapi/?cdxRedirectPath=/");
          }

          // Allow SdkKeysService to handle its own retries/auth branching.
          const isSdkKeysEndpoint = error.config?.url?.includes('/metadata/sdk-keys');
          if (isSdkKeysEndpoint) {
            return Promise.reject(error);
          }

          // Allow file upload requests to handle their own retry/fallback and
          // partial-failure UX instead of being hard-redirected to /error-page.
          if (error.config?.skipGlobalErrorRedirect) {
            return Promise.reject(error);
          }

          if (error.response.status == 500 || error.response.status == 503) {
            window.location.href = "/error-page";
            if (error.response.data) {
              error.response.data.message = "Internal server error";
            }
          } else if (error.response.status == 417) {
            if (error.response.data) {
              error.response.data.message = error.response.data.message;
            }
          } else if (error.response.status == 422) {
            if (error.response.data && Array.isArray(error.response.data.errors) && error.response.data.errors.length > 0) {
              let errMsg = [];
              for (let err of error.response.data.errors) {
                let mergeobj = err.field + " " + err.message;
                errMsg.push(mergeobj);
              }
              let conStr = errMsg.join(", ");
              error.response.data.message = conStr;
            } else if (error.response.data) {
              error.response.data.message = error.response.data.message;
            }
          } else if (error.response.status == 401 || error.response.status == 403) {
            window.location.href = "/unauthorized";
            return Promise.reject(error);
          }
        }
        return error.response;
      }
    );
  },
};

